import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { Page, QuoteForm } from "../components/site";
import { plaatsFoto } from "../lib/plaats-foto";
import {
  SITE,
  diensten,
  euro,
  lokaalBedrag,
  lokaalBereik,
  plaatsBySlug,
  plaatsFaq,
  plaatsen,
  voorrijkosten,
} from "../lib/seo-data";

export const Route = createFileRoute("/plaatsen/$plaats")({
  loader: ({ params }) => {
    const plaats = plaatsBySlug(params.plaats);
    if (!plaats) throw notFound();
    return { plaats };
  },
  head: ({ params, loaderData }) => {
    const p = loaderData?.plaats;
    if (!p) {
      return { meta: [{ title: "Pagina niet gevonden" }, { name: "robots", content: "noindex" }] };
    }
    const title =
      p.lokaal?.titel ?? `Elektricien ${p.naam}: kosten en tarieven 2026 | offerto`;
    const description =
      p.lokaal?.beschrijving ??
      `Wat kost een elektricien in ${p.naam}? Uurtarief € 50 tot € 90, voorrijkosten ${voorrijkosten(p.slug)}. Bekijk de kosten per klus: groepenkast, laadpaal, storing en verlichting. Vergelijk gratis offertes in ${p.provincie}.`;
    const url = `${SITE}/plaatsen/${params.plaats}`;
    const foto = plaatsFoto(p.slug, p.naam);
    const fotoUrl = `${SITE}${foto.url}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:locale", content: "nl_NL" },
        { name: "geo.placename", content: p.naam },
        { name: "geo.region", content: "NL" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: fotoUrl },
        { property: "og:image:alt", content: foto.alt },
        { name: "twitter:image", content: fotoUrl },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": `${url}#localbusiness`,
            name: `offerto ${p.naam}`,
            description,
            url,
            areaServed: { "@type": "City", name: p.naam, address: { "@type": "PostalAddress", addressLocality: p.naam, addressRegion: p.provincie, addressCountry: "NL" } },
            address: { "@type": "PostalAddress", addressLocality: p.naam, addressRegion: p.provincie, addressCountry: "NL" },
            priceRange: "€€",
            currenciesAccepted: "EUR",
            image: fotoUrl,
            openingHoursSpecification: [
              {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                opens: "08:00",
                closes: "18:00",
              },
            ],
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: `Elektricienklussen in ${p.naam}`,
              itemListElement: diensten.map((d) => ({
                "@type": "Offer",
                url: `${SITE}/elektricien/${d.slug}/${p.slug}`,
                itemOffered: { "@type": "Service", name: `${d.naam} in ${p.naam}`, serviceType: d.naam },
                priceSpecification: {
                  "@type": "PriceSpecification",
                  priceCurrency: "EUR",
                  minPrice: lokaalBedrag(d.van, p.slug),
                  maxPrice: lokaalBedrag(d.tot, p.slug),
                },
              })),
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: plaatsFaq(p).map((f) => ({
              "@type": "Question",
              name: f.v,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Elektricien per plaats", item: `${SITE}/plaatsen` },
              { "@type": "ListItem", position: 3, name: `Elektricien ${p.naam}`, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: PlaatsPagina,
});

function PlaatsPagina() {
  const { plaats } = Route.useLoaderData();
  const index = plaatsen.findIndex((p) => p.slug === plaats.slug);
  const regio = [1, 2, 3, 4, 5, 6].map((n) => plaatsen[(index + n) % plaatsen.length]!);
  const foto = plaatsFoto(plaats.slug, plaats.naam);

  return (
    <Page
      title={`Elektricien in ${plaats.naam}`}
      intro={`Vind dé elektricien voor je project in ${plaats.naam}. Vergelijk gratis en vrijblijvend tot 5 offertes van betrouwbare bedrijven uit de regio. Binnen een minuut aangevraagd.`}
    >
      <nav className="mt-6 text-sm text-muted-foreground" aria-label="Kruimelpad">
        <Link to="/plaatsen" className="hover:text-foreground">
          Elektricien per plaats
        </Link>
        <span className="px-2">/</span>
        <span className="text-foreground">Elektricien {plaats.naam}</span>
      </nav>

      <figure className="mt-6 overflow-hidden rounded-lg border border-border bg-card">
        <img
          src={foto.url}
          alt={foto.alt}
          width={1200}
          height={630}
          fetchPriority="high"
          loading="eager"
          className="h-auto w-full object-cover"
        />
        <figcaption className="px-4 py-3 text-xs text-muted-foreground">
          Werk van een aangesloten elektricien in {plaats.naam} en omgeving.
        </figcaption>
      </figure>

      <div className="mt-8 grid min-w-0 gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold">Elektrawerk in {plaats.naam}</h2>
          <p className="mt-3 text-muted-foreground">
            {plaats.naam} ligt in {plaats.provincie} en telt {plaats.inwoners} inwoners. De stad kent{" "}
            {plaats.woningbeeld}. Elektriciens in {plaats.naam} werken daardoor veel aan het
            verzwaren van groepenkasten, het bijplaatsen van stopcontacten en het veilig aansluiten
            van laadpalen en zonnepanelen.
          </p>
          <p className="mt-3 text-muted-foreground">
            We hebben vakmensen die werken in {plaats.wijken.join(", ")} en de omliggende gemeenten.
          </p>

          {plaats.lokaal ? (
            <>
              {plaats.lokaal.intro.map((alinea) => (
                <p key={alinea.slice(0, 40)} className="mt-3 text-muted-foreground">
                  {alinea}
                </p>
              ))}

              <h2 className="mt-10 text-2xl font-bold">
                Elektricien per wijk in {plaats.naam}
              </h2>
              <ul className="mt-4 space-y-3">
                {plaats.lokaal.wijkTekst.map((w) => (
                  <li key={w.wijk} className="rounded-lg border border-border bg-card p-5">
                    <h3 className="font-bold">{w.wijk}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{w.tekst}</p>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-sm text-muted-foreground">{plaats.lokaal.omliggend}</p>

              <h2 className="mt-10 text-2xl font-bold">
                Meest aangevraagde klussen in {plaats.naam}
              </h2>
              <div className="mt-4 space-y-3">
                {plaats.lokaal.topKlussen.map((k) => {
                  const dienst = diensten.find((d) => d.slug === k.dienstSlug);
                  if (!dienst) return null;
                  return (
                    <div
                      key={k.dienstSlug}
                      className="rounded-lg border border-border bg-card p-5"
                    >
                      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3 max-[400px]:grid-cols-1">
                        <h3 className="min-w-0 font-bold">
                          <Link
                            to="/elektricien/$dienst/$plaats"
                            params={{ dienst: dienst.slug, plaats: plaats.slug }}
                            className="hover:text-primary"
                          >
                            {dienst.naam} in {plaats.naam}
                          </Link>
                        </h3>
                        <span className="whitespace-nowrap text-xs font-bold text-primary">
                          {lokaalBereik(dienst, plaats.slug)}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">{k.tekst}</p>
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}

          <h2 className="mt-10 text-2xl font-bold">Wat kost een elektricien in {plaats.naam}?</h2>
          <p className="mt-3 text-muted-foreground">
            Een erkende elektricien in {plaats.naam} rekent gemiddeld{" "}
            <strong className="text-foreground">
              {euro(lokaalBedrag(55, plaats.slug))} tot {euro(lokaalBedrag(85, plaats.slug))} per
              uur
            </strong>
            , plus {voorrijkosten(plaats.slug)} voorrijkosten. Een bezoek van een half uur tot een
            uur komt daarmee meestal uit op{" "}
            {euro(lokaalBedrag(80, plaats.slug))} tot {euro(lokaalBedrag(150, plaats.slug))}.
            Hieronder zie je de richtprijzen per klus in {plaats.naam}.
          </p>

          <div className="mt-5 overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[480px] border-collapse text-sm">
              <caption className="sr-only">Kosten per klus in {plaats.naam}</caption>
              <thead className="bg-secondary text-left">
                <tr>
                  <th scope="col" className="px-4 py-3 font-semibold">
                    Klus
                  </th>
                  <th scope="col" className="px-4 py-3 font-semibold">
                    Kosten in {plaats.naam}
                  </th>
                  <th scope="col" className="px-4 py-3 font-semibold">
                    Gemiddeld
                  </th>
                </tr>
              </thead>
              <tbody>
                {diensten.map((d) => (
                  <tr key={d.slug} className="border-t border-border bg-card">
                    <th scope="row" className="px-4 py-3 text-left font-medium">
                      <Link
                        to="/elektricien/$dienst/$plaats"
                        params={{ dienst: d.slug, plaats: plaats.slug }}
                        className="hover:text-primary"
                      >
                        {d.naam}
                      </Link>
                    </th>
                    <td className="whitespace-nowrap px-4 py-3 font-semibold">
                      {lokaalBereik(d, plaats.slug)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                      {euro(lokaalBedrag(d.gemiddeld, plaats.slug))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Richtprijzen inclusief btw, bijgewerkt in 2026 en omgerekend naar het prijsniveau van{" "}
            {plaats.naam}. Vergelijk ze met de{" "}
            <Link to="/diensten" className="font-medium text-primary hover:underline">
              landelijke kosten per elektricienklus
            </Link>
            .
          </p>

          <h2 className="mt-10 text-2xl font-bold">Klussen in {plaats.naam}</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 [&>a]:block">
            {diensten.map((d) => (
              <Link
                key={d.slug}
                to="/elektricien/$dienst/$plaats"
                params={{ dienst: d.slug, plaats: plaats.slug }}
                className="rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
              >
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3 max-[400px]:grid-cols-1">
                  <h3 className="min-w-0 font-bold">
                    {d.naam} in {plaats.naam}
                  </h3>
                  <span className="whitespace-nowrap text-xs font-bold text-primary">
                    {lokaalBereik(d, plaats.slug)}
                  </span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{d.kort}</p>
              </Link>
            ))}
          </div>

          <h2 className="mt-10 text-2xl font-bold">
            Landelijke kosten per klus
          </h2>
          <p className="mt-2 text-muted-foreground">
            Wil je weten wat een klus gemiddeld in heel Nederland kost? Bekijk dan het
            kostenoverzicht per klus.
          </p>
          <ul className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
            {diensten.map((d) => (
              <li key={d.slug}>
                <Link
                  to="/elektricien/$dienst"
                  params={{ dienst: d.slug }}
                  className="text-muted-foreground hover:text-primary"
                >
                  Wat kost {d.naam.toLowerCase()}? {d.prijs}
                </Link>
              </li>
            ))}
          </ul>

          <h2 className="mt-10 text-2xl font-bold">
            Veelgestelde vragen over een elektricien in {plaats.naam}
          </h2>
          <div className="mt-4 space-y-3">
            {plaatsFaq(plaats).map((f) => (
              <details
                key={f.v}
                className="rounded-lg border border-border bg-card p-5 [&_summary]:cursor-pointer"
              >
                <summary className="font-bold">{f.v}</summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        <aside>
          <QuoteForm compact />
          <div className="mt-8 rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold">Plaatsen in de buurt</h2>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              {regio.map((p) => (
                <li key={p.slug}>
                  <Link
                    to="/plaatsen/$plaats"
                    params={{ plaats: p.slug }}
                    className="hover:text-foreground"
                  >
                    Elektricien {p.naam}: tarieven en voorrijkosten
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/plaatsen" className="font-medium text-primary hover:underline">
                  Alle plaatsen met lokale tarieven
                </Link>
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </Page>
  );
}
